import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Save, Layout, MessageSquare, Edit2, Trash2, Plus, X } from 'lucide-react';

const CardModal = ({ isOpen, onClose, onSave, card, index }) => {
    const [formData, setFormData] = useState({ title: '', description: '', is_active: true });

    useEffect(() => {
        if (card) {
            setFormData({ ...card, is_active: card.is_active ?? true });
        } else {
            setFormData({ title: '', description: '', is_active: true });
        }
    }, [card, isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50">
                    <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        {card ? `Edit Card ${index + 1}` : 'Add New Card'}
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-white rounded-full transition-colors">
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>
                <div className="p-8 space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Card Title</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all font-medium"
                            placeholder="Enter card title..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Card Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all font-medium min-h-[120px]"
                            placeholder="Enter card description..."
                        />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div>
                            <p className="font-bold text-slate-700">Display Status</p>
                            <p className="text-xs text-slate-500">Show or hide this card on the blog page</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.is_active ? 'bg-primary-600' : 'bg-slate-300'}`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-1'}`}
                            />
                        </button>
                    </div>
                </div>
                <div className="p-6 border-t border-slate-100 flex justify-end gap-3 bg-slate-50/50">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-white transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSave(formData)}
                        className="px-8 py-2.5 bg-[#283079] text-white rounded-xl font-bold hover:bg-[#1e2560] transition-all shadow-lg shadow-indigo-100"
                    >
                        {card ? 'Update Card' : 'Add Card'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden border border-slate-100 animate-in fade-in zoom-in duration-200">
                <div className="p-8 text-center">
                    <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Trash2 className="w-10 h-10 text-rose-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-3">Delete Card?</h3>
                    <p className="text-slate-500 font-medium mb-8 leading-relaxed">
                        Are you sure you want to delete <span className="text-slate-800 font-bold">"{title}"</span>? This action cannot be undone.
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-4 rounded-2xl font-bold text-slate-600 bg-slate-50 hover:bg-slate-100 transition-all border border-slate-200"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            className="flex-1 px-6 py-4 bg-rose-600 text-white rounded-2xl font-bold hover:bg-rose-700 transition-all shadow-lg shadow-rose-100"
                        >
                            Delete Now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AdminBlogCTASettingsPage = () => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [modalConfig, setModalConfig] = useState({ isOpen: false, card: null, index: -1 });
    const [deleteModalConfig, setDeleteModalConfig] = useState({ isOpen: false, index: -1, title: '' });
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        content_data: {
            cards: []
        }
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/blog/cta-settings');
            if (response.data.success && response.data.data) {
                setFormData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching blog CTA settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            await axios.put('/api/admin/blog/cta-settings', formData);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error updating blog CTA settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveCard = (cardData) => {
        const newCards = [...formData.content_data.cards];
        if (modalConfig.index !== -1) {
            newCards[modalConfig.index] = cardData;
        } else {
            newCards.push(cardData);
        }
        setFormData({
            ...formData,
            content_data: {
                ...formData.content_data,
                cards: newCards
            }
        });
        setModalConfig({ isOpen: false, card: null, index: -1 });
    };

    const handleDeleteCard = (index) => {
        const card = formData.content_data.cards[index];
        setDeleteModalConfig({
            isOpen: true,
            index,
            title: card.title || `Card ${index + 1}`
        });
    };

    const confirmDelete = () => {
        const newCards = formData.content_data.cards.filter((_, i) => i !== deleteModalConfig.index);
        setFormData({
            ...formData,
            content_data: {
                ...formData.content_data,
                cards: newCards
            }
        });
        setDeleteModalConfig({ isOpen: false, index: -1, title: '' });
        toast.success('Card removed from list');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6 pb-20">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Blog CTA Settings</h1>
                    <p className="text-slate-500 mt-1">Manage the "Learn More" section on blog pages</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Main Header Settings */}
                <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-primary-50 rounded-lg">
                            <Layout className="w-5 h-5 text-primary-600" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-800">Section Header</h2>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Section Title</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all font-medium"
                                placeholder="e.g., Want to Learn More About Home Loans?"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Section Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all font-medium min-h-[120px]"
                                placeholder="Enter introductory text..."
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Feature Cards Settings */}
                <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary-50 rounded-lg">
                                <MessageSquare className="w-5 h-5 text-primary-600" />
                            </div>
                            <h2 className="text-xl font-bold text-slate-800">Feature Cards</h2>
                        </div>
                        <button
                            type="button"
                            onClick={() => setModalConfig({ isOpen: true, card: null, index: -1 })}
                            className="flex items-center gap-2 bg-primary-50 hover:bg-primary-100 text-primary-600 px-5 py-2.5 rounded-xl font-bold transition-all border border-primary-100"
                        >
                            <Plus className="w-5 h-5" />
                            Add New Card
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {formData.content_data.cards.map((card, index) => (
                            <div key={index} className="group relative p-6 bg-slate-50 rounded-[1.5rem] border border-slate-100 hover:border-primary-200 hover:bg-white transition-all duration-300">
                                <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        type="button"
                                        onClick={() => setModalConfig({ isOpen: true, card, index })}
                                        className="p-2 bg-white text-primary-600 rounded-lg shadow-sm border border-slate-100 hover:bg-primary-50 transition-colors"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteCard(index)}
                                        className="p-2 bg-white text-rose-600 rounded-lg shadow-sm border border-slate-100 hover:bg-rose-50 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="space-y-3 pr-8">
                                    <span className="inline-block px-3 py-1 bg-white rounded-full text-[10px] font-black text-primary-600 uppercase tracking-widest border border-slate-100 shadow-sm">
                                        Card {index + 1}
                                    </span>
                                    {card.is_active === false && (
                                        <span className="inline-block px-2 py-0.5 bg-rose-50 text-rose-600 text-[10px] font-bold rounded-md border border-rose-100 uppercase tracking-wider ml-2">
                                            Hidden
                                        </span>
                                    )}
                                    <h3 className="font-bold text-slate-800 text-lg line-clamp-1">{card.title}</h3>
                                    <p className="text-sm text-slate-500 leading-relaxed line-clamp-3 font-medium">
                                        {card.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {formData.content_data.cards.length === 0 && (
                        <div className="text-center py-12 bg-slate-50 rounded-[1.5rem] border-2 border-dashed border-slate-200">
                            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                            <p className="text-slate-500 font-medium">No feature cards added yet.</p>
                            <button
                                type="button"
                                onClick={() => setModalConfig({ isOpen: true, card: null, index: -1 })}
                                className="text-primary-600 font-bold mt-2 hover:underline"
                            >
                                Add your first card
                            </button>
                        </div>
                    )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={submitting}
                        className="flex items-center gap-2 bg-[#283079] hover:bg-[#1e2560] text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-lg shadow-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 active:translate-y-0"
                    >
                        {submitting ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                            <Save className="w-5 h-5" />
                        )}
                        {submitting ? 'Saving Changes...' : 'Save All Settings'}
                    </button>
                </div>
            </form>

            <CardModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig({ isOpen: false, card: null, index: -1 })}
                onSave={handleSaveCard}
                card={modalConfig.card}
                index={modalConfig.index}
            />

            <DeleteConfirmModal
                isOpen={deleteModalConfig.isOpen}
                onClose={() => setDeleteModalConfig({ isOpen: false, index: -1, title: '' })}
                onConfirm={confirmDelete}
                title={deleteModalConfig.title}
            />
        </div>
    );
};

export default AdminBlogCTASettingsPage;
